import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { AbstractControl } from '@angular/forms'
import { ButtonDisplayType, DestroySubscribers, MOMENT_DATE_FORMAT_NORMAL } from '@ftr/foundation'
import * as Flatpickr from 'flatpickr'
import moment, { Moment } from 'moment-timezone'
import { Observable, takeUntil } from 'rxjs'
import { FlatpickrFactory } from './flatpickr.factory'

// Just to catch anyone entering 200AD :-)
const MIN_DATE = new Date(1900, 0, 1)
let nextId = 0

/**
 * A date picker component which outputs a moment value
 */
@Component({
  selector: 'ftr-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class DatePickerComponent extends DestroySubscribers implements OnInit, OnDestroy {
  @Input() label: string
  @Input() ariaLabel?: string
  @Input() required = true
  @Input() control: AbstractControl
  @Input() highlightError: Observable<boolean>
  @Input() submitAttempted = false
  @Input() maxDate: Observable<Moment> | Moment
  @Input() id = `ftr-date-picker-${nextId++}`
  @Input() placeholder = ''
  @Input() disablePicker = false
  /**
   * Shows/hides the css/text for validation hints
   */
  @Input() showValidation = true
  /**
   * When clearDate is supplied we show the X button to clear the date when a value is present,
   *   and when the observable is fired we clear any values
   */
  @Input() clearDate: Observable<void> | undefined

  @ViewChild('datePicker', { static: true }) inputElementRef: ElementRef

  hasValue = false
  private datePicker: Flatpickr.default.Instance
  readonly buttonDisplayType = ButtonDisplayType

  constructor(private readonly flatpickrFactory: FlatpickrFactory) {
    super()
  }

  ngOnInit(): void {
    this.datePicker = this.flatpickrFactory.create(this.inputElementRef.nativeElement, {
      allowInput: true,
      dateFormat: 'M d, Y',
      minDate: MIN_DATE,
      onOpen: this.onDatePickerOpen.bind(this),
      onChange: this.onDatePickerChange.bind(this),
      onClose: this.onDatePickerClose.bind(this),
      clickOpens: !this.disablePicker,
    })
    this.setMaxDate()

    if (this.control.value) {
      this.datePicker.setDate(this.control.value.toDate(), false)
      this.hasValue = true
    }

    this.control.valueChanges.pipe(takeUntil(this.finalize)).subscribe(value => {
      if (!value) {
        this.datePicker.clear()
        this.hasValue = false
      } else if (moment.isMoment(value)) {
        this.datePicker.setDate(value.toDate(), false)
        this.hasValue = true
      }
    })

    if (this.clearDate) {
      this.clearDate.pipe(takeUntil(this.finalize)).subscribe(() => this.onClear())
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.datePicker.destroy()
  }

  onClear(): void {
    this.control.setValue(undefined)
    this.datePicker.clear()
    this.hasValue = false
  }

  onDatePickerOpen(): void {
    /**
     * The date picker is opened when the input element is clicked.
     * This sets the focus to the input element.
     * The calendar date picker doesn't work well if the input element is in focus
     * so the user ends up having to click the date twice, first to make it lose focus
     * and then to actually select the date.
     * Here we simply force the blur of the input element so the user doesn't have to
     * click twice.
     */
    this.inputElementRef.nativeElement.blur()
  }

  onDatePickerChange(_: Date[], dateStr: string): void {
    // Flatpickr called onchange once when initialized so we guard against making a charge to avoid ExpressionChanged
    if (!this.control.pristine || dateStr !== '') {
      this.setControlValue(dateStr)
    }
  }

  onDatePickerClose(): void {
    this.setControlTouched()
  }

  onInputElementBlur(event: Event): void {
    const target = event.target as HTMLInputElement

    this.setControlValue(target.value)
    /**
     * Because we force blur the input element when the date picker is opened
     * (see onDatePickerOpen) we don't want it to consider the control already
     * touched as the user has yet to attempt to make a selection.
     * We know that this only occurs when the date picker is opened and the
     * value is falsy. In that circumstance we don't want to set the control
     * to touched just yet.
     */
    if (!this.datePicker.isOpen || target.value) {
      this.setControlTouched()
    }
  }

  /**
   * Prevent flatpickr from triggering its own 'on enter' event here to avoid
   * e.g. 8/2/89 from becoming Dec 3 2013 (one year less for each character in the dateFormat)
   */
  onInputElementKeyDown(event: Event): void {
    const target = event.target as HTMLInputElement
    event.preventDefault()
    event.stopImmediatePropagation()
    this.setControlValue(target.value)
  }

  private setControlTouched(): void {
    this.control.markAsTouched()
  }

  private setControlValue(value: string): void {
    this.hasValue = !!value

    const date = this.hasValue ? moment(value, MOMENT_DATE_FORMAT_NORMAL, true) : undefined
    if (date && date.isValid()) {
      this.control.setValue(date)
      return
    }

    const dateWithoutFormat = this.hasValue ? moment(value) : undefined
    if (dateWithoutFormat && dateWithoutFormat.isValid()) {
      this.control.setValue(dateWithoutFormat)
    } else {
      this.control.setValue(date)
    }
  }

  private setMaxDate(): void {
    if (this.maxDate instanceof Observable) {
      this.maxDate
        .pipe(takeUntil(this.finalize))
        .subscribe(momentInstance => this.datePicker.set('maxDate', momentInstance.toDate()))
    } else if (moment.isMoment(this.maxDate)) {
      this.datePicker.set('maxDate', this.maxDate.toDate())
    }
  }
}
